import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Aperture, Eye, EyeOff, LockKeyhole, UserRound } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { ApiError, api } from "../lib/api";
import { Button, Field, InlineError, Input } from "../components/ui";

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const mutation = useMutation({
    mutationFn: api.login,
    onSuccess: async (user) => {
      queryClient.setQueryData(["session"], user);
      const target = search.redirect?.startsWith("/") && !search.redirect.startsWith("//") ? search.redirect : "/tasks";
      await navigate({ to: target, replace: true });
    }
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate({ username: username.trim().toLowerCase(), password });
  };

  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage === "zh-CN" ? "zh-CN" : "en";
    document.title = t("login.meta.title");
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", t("login.meta.description"));
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", "#fbfcfb");
  }, [i18n.resolvedLanguage, t]);

  const mutationError = mutation.isError
    ? mutation.error instanceof ApiError && mutation.error.status === 401
      ? t("login.errors.invalidCredentials")
      : mutation.error instanceof ApiError && mutation.error.status === 400
        ? t("login.errors.invalidRequest")
        : mutation.error instanceof TypeError
          ? t("login.errors.network")
          : t("login.errors.unknown")
    : undefined;

  return (
    <main className="login-page">
      <section className="login-form-side">
        <div className="login-header">
          <Link className="login-brand" to="/">
            <span className="brand-mark"><Aperture size={17} strokeWidth={2} /></span>
            <strong>{t("login.brand")}</strong>
          </Link>
          <LanguageSwitcher />
        </div>
        <form className="login-form" onSubmit={submit}>
          <h1 className="m-0 text-2xl font-bold">{t("login.title")}</h1>
          <p className="mb-7 mt-2 text-sm text-[var(--text-muted)]">{t("login.description")}</p>
          <div className="grid gap-4">
            {mutationError && <InlineError message={mutationError} />}
            <Field label={t("login.fields.username")}>
              <div className="relative">
                <UserRound className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" size={16} strokeWidth={1.8} />
                <Input className="pl-9" name="username" autoComplete="username" required value={username} onChange={(event) => setUsername(event.target.value.toLowerCase())} placeholder={t("login.fields.usernamePlaceholder")} />
              </div>
            </Field>
            <Field label={t("login.fields.password")}>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-faint)]" size={16} strokeWidth={1.8} />
                <Input className="px-9" type={showPassword ? "text" : "password"} name="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
                <button type="button" className="absolute right-1 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-[5px] text-[var(--text-muted)] hover:bg-[var(--surface-muted)]" aria-label={t(showPassword ? "login.actions.hidePassword" : "login.actions.showPassword")} onClick={() => setShowPassword((value) => !value)}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </Field>
            <Button className="mt-1 w-full" type="submit" variant="primary" disabled={mutation.isPending}>{t(mutation.isPending ? "login.actions.submitting" : "login.actions.submit")}</Button>
            <p className="m-0 text-center text-xs text-[var(--text-muted)]">{t("login.registrationPrompt")} <Link className="table-link" to="/register" search={{ redirect: search.redirect }}>{t("login.registrationAction")}</Link></p>
          </div>
        </form>
        <div className="text-xs text-[var(--text-faint)]">{t("login.accountSecurity")}</div>
      </section>
      <section className="login-visual" aria-label={t("login.signal.ariaLabel")}>
        <div className="login-signal">
          <h2 className="mb-9 mt-0 text-3xl font-semibold leading-tight">{t("login.signal.title")}</h2>
          <div className="login-signal-line" />
          <div className="login-signal-row"><span>gpt-image-2</span><div className="login-signal-bar"><span style={{ width: "88%" }} /></div><span>{t("login.signal.image")}</span></div>
          <div className="login-signal-line" />
          <div className="login-signal-row"><span>seedance2-pro</span><div className="login-signal-bar"><span style={{ width: "72%" }} /></div><span>{t("login.signal.video")}</span></div>
          <div className="login-signal-line" />
          <div className="login-signal-row"><span>seedance2-fast</span><div className="login-signal-bar"><span style={{ width: "93%" }} /></div><span>{t("login.signal.video")}</span></div>
          <div className="login-signal-line" />
        </div>
      </section>
    </main>
  );
}
